import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Spinner, Alert } from 'react-bootstrap';
import { FaChartLine, FaArrowDown, FaArrowUp } from 'react-icons/fa';
import axios from 'axios';
import TradingForm from '../TradingForm';

const API_BASE = 'https://18z7zyxvn4.execute-api.us-east-2.amazonaws.com/robovest-api';

const TradingStrategies = () => {
  const [tradeInfo, setTradeInfo]   = useState(null);
  const [isLoading, setIsLoading]   = useState(true);
  const [isSaving, setIsSaving]     = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error'
  const [fetchError, setFetchError] = useState(null);

  const authHeaders = () => ({
    Authorization: 'Bearer ' + sessionStorage.getItem('jwt'),
    'Content-Type': 'Application/json',
    'Access-Control-Allow-Origin': '*',
  });

  useEffect(() => {
    axios
      .get(API_BASE, { headers: authHeaders() })
      .then((res) => {
        setTradeInfo(res.data.trade);
        setIsLoading(false);
      })
      .catch((err) => {
        setFetchError('Failed to load strategy: ' + err.message);
        setIsLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFormSubmit = (formData) => {
    setIsSaving(true);
    setSaveStatus(null);
    axios
      .post(API_BASE, { trade: formData }, { headers: authHeaders() })
      .then(() => {
        setTradeInfo(formData);
        setSaveStatus('success');
      })
      .catch(() => setSaveStatus('error'))
      .finally(() => setIsSaving(false));
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (fetchError) {
    return <Alert variant="danger">{fetchError}</Alert>;
  }

  return (
    <>
      <h2 className="display-6 mb-4">Trading Strategies</h2>

      {/* ── Current strategy summary ── */}
      {tradeInfo && (
        <Card className="mb-4">
          <Card.Header className="d-flex align-items-center gap-2 fw-semibold">
            <FaChartLine className="text-primary" />
            Current Strategy — <span className="text-primary">{tradeInfo.symbol}</span>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col xs={12} md={6}>
                <div className="p-3 rounded border h-100">
                  <div className="d-flex align-items-center gap-2 mb-2 fw-semibold">
                    <FaArrowDown className="text-success" /> Buy Rules
                  </div>
                  <div className="small text-muted mb-1">
                    Price range: <strong className="text-body">${tradeInfo.floor} – ${tradeInfo.ceiling}</strong>
                  </div>
                  <div className="small text-muted mb-1">
                    Lookback: <strong className="text-body">{tradeInfo.lookBackDays} days</strong>
                    {' '}/ Focus: <strong className="text-body">{tradeInfo.focusDays} days</strong>
                  </div>
                  <div className="small text-muted mb-1">
                    Type: <Badge bg="secondary" className="fw-normal">{tradeInfo.lookBackType}</Badge>
                  </div>
                  <div className="small text-muted">
                    Buy cheaper by: <strong className="text-body">{tradeInfo.cheaperBuyPercent}%</strong>
                  </div>
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div className="p-3 rounded border h-100">
                  <div className="d-flex align-items-center gap-2 mb-2 fw-semibold">
                    <FaArrowUp className="text-danger" /> Sell Rules
                  </div>
                  <div className="small text-muted">
                    Take profit at: <strong className="text-body">{tradeInfo.upSellPercent}%</strong>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* ── Edit form ── */}
      {saveStatus === 'success' && (
        <Alert variant="success" dismissible onClose={() => setSaveStatus(null)}>
          Strategy updated successfully.
        </Alert>
      )}
      {saveStatus === 'error' && (
        <Alert variant="danger" dismissible onClose={() => setSaveStatus(null)}>
          Failed to save. Please try again.
        </Alert>
      )}

      <TradingForm
        initialValues={tradeInfo}
        onSubmit={handleFormSubmit}
        isSaving={isSaving}
      />
    </>
  );
};

export default TradingStrategies;
